// ============================================================================
// Tab title — folder name + cross-tab ordinal disambiguation
// ============================================================================
// Sets document.title to basename(activeCwd) so multiple Devin tabs are
// distinguishable. When ≥2 tabs share the same folder name, appends an
// ordinal: "foo", "foo (2)", "foo (3)". Coordination is via BroadcastChannel
// with a localStorage fallback. The active session's displayName (from the
// gateway's session_names mapping) is preferred over a raw basename.

import { state } from "./state.js";

const TAB_REGISTRY_KEY = "devin-tab-registry";
const TAB_HEARTBEAT_MS = 5000;
const TAB_STALE_MS = 15000;

let tabInstanceId = null;
let tabFolderName = "";
let tabDisplayName = "";
const tabRegistry = new Map(); // instanceId -> { folderName, seenAt }
let tabChannel = null;
let tabHeartbeatTimer = null;

export function tabBasename(p) {
  if (!p) return "";
  const trimmed = p.replace(/[\/\\]+$/, "");
  if (!trimmed) return "";
  const parts = trimmed.split(/[\/\\]/);
  return parts[parts.length - 1] || "";
}

/** Return the current display name (preferred) or basename of the active cwd. */
export function tabCurrentName() {
  return tabDisplayName || tabBasename(state.activeCwd);
}

function tabReadRegistryStorage() {
  try { return JSON.parse(localStorage.getItem(TAB_REGISTRY_KEY) || "{}"); }
  catch { return {}; }
}

function tabWriteRegistryStorage() {
  try {
    const obj = {};
    for (const [id, entry] of tabRegistry) obj[id] = entry;
    localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(obj));
  } catch { /* ignore */ }
}

function tabPruneStale() {
  const now = Date.now();
  let changed = false;
  for (const [id, entry] of tabRegistry) {
    if (now - entry.seenAt > TAB_STALE_MS) {
      tabRegistry.delete(id);
      changed = true;
    }
  }
  return changed;
}

function tabComputeOrdinal() {
  if (!tabFolderName) return 1;
  const peers = [];
  for (const [id, entry] of tabRegistry) {
    if (entry.folderName === tabFolderName) peers.push(id);
  }
  peers.sort();
  const pos = peers.indexOf(tabInstanceId);
  return pos >= 0 ? pos + 1 : 1;
}

export function updateTabTitle() {
  const name = tabDisplayName || tabBasename(state.activeCwd);
  const badge = state.unreadEvents > 0 ? `(${state.unreadEvents}) ` : "";
  if (!name) {
    document.title = badge.trim();
    return;
  }
  tabFolderName = name;
  const ordinal = tabComputeOrdinal();
  const base = `${name} - Gateway`;
  const titled = ordinal > 1 ? `${base} (${ordinal})` : base;
  document.title = badge + titled;
}

export function tabAnnounce() {
  if (!tabInstanceId) return;
  tabRegistry.set(tabInstanceId, { folderName: tabFolderName, seenAt: Date.now() });
  tabWriteRegistryStorage();
  if (tabChannel) {
    try { tabChannel.postMessage({ kind: "hello", instanceId: tabInstanceId, folderName: tabFolderName }); }
    catch { /* ignore */ }
  }
}

function tabRecompute() {
  tabPruneStale();
  updateTabTitle();
}

function tabOnMessage(ev) {
  const data = ev.data;
  if (!data || !data.kind) return;
  if (data.kind === "hello") {
    tabRegistry.set(data.instanceId, { folderName: data.folderName, seenAt: Date.now() });
    tabRecompute();
  } else if (data.kind === "bye") {
    tabRegistry.delete(data.instanceId);
    tabRecompute();
  } else if (data.kind === "who") {
    tabAnnounce();
  }
}

function tabOnStorage(ev) {
  if (ev.key !== TAB_REGISTRY_KEY) return;
  const stored = tabReadRegistryStorage();
  tabRegistry.clear();
  for (const [id, entry] of Object.entries(stored)) {
    tabRegistry.set(id, entry);
  }
  tabRecompute();
}

function tabOnPageHide() {
  if (!tabInstanceId) return;
  tabRegistry.delete(tabInstanceId);
  tabWriteRegistryStorage();
  if (tabChannel) {
    try { tabChannel.postMessage({ kind: "bye", instanceId: tabInstanceId }); }
    catch { /* ignore */ }
  }
}

export function initTabTitle() {
  tabInstanceId = (crypto && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + "_" + Math.random().toString(36).slice(2));
  const stored = tabReadRegistryStorage();
  for (const [id, entry] of Object.entries(stored)) {
    tabRegistry.set(id, entry);
  }
  try {
    tabChannel = new BroadcastChannel("devin-tab-titles");
    tabChannel.onmessage = tabOnMessage;
  } catch { tabChannel = null; }
  window.addEventListener("storage", tabOnStorage);
  window.addEventListener("pagehide", tabOnPageHide);
  tabHeartbeatTimer = setInterval(() => {
    tabAnnounce();
    tabRecompute();
  }, TAB_HEARTBEAT_MS);
  if (tabChannel) {
    try { tabChannel.postMessage({ kind: "who" }); }
    catch { /* ignore */ }
  }
  tabAnnounce();
  updateTabTitle();
}

/**
 * Update the active cwd and tab title. Called when a session starts or
 * the workspace folder changes. The onCwdChange callback handles
 * file-viewer flushing and context meter reset (those depend on modules
 * that aren't extracted yet).
 */
export function setActiveCwd(cwd, displayName, onCwdChange) {
  const prevCwd = state.activeCwd;
  state.activeCwd = cwd;
  tabDisplayName = displayName || "";
  tabFolderName = displayName || tabBasename(cwd);
  tabAnnounce();
  updateTabTitle();
  if (prevCwd !== cwd && onCwdChange) {
    onCwdChange(prevCwd, cwd);
  }
}
